"use client";

import React, { useEffect, useRef, useState } from "react";
import { Wrench, Car, ShieldCheck, Zap, Search, Gauge } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const inspectionPoints = [{
  id: 1,
  title: "Cold-Chain Integrity",
  desc: "We maintain a strict -10°F environment from the moment our catch is processed until it reaches your doorstep in The Villages.",
  iconName: "zap"
}, {
  id: 2,
  title: "Sustainable Sourcing",
  desc: "Sourced exclusively from Florida waters following strict FWC guidelines to protect our local marine ecosystems and future harvests.",
  iconName: "shield"
}, {
  id: 3,
  title: "Dedicated Local Delivery",
  desc: "Exclusively serving our neighbors with dedicated local routes, ensuring punctual arrival and maintaining the frozen standard.",
  iconName: "car"
}, {
  id: 4,
  title: "Flash-Frozen Freshness",
  desc: "Our rapid-freeze technology locks in nutrients and delicate ocean flavors at the peak of freshness, surpassing traditional counters.",
  iconName: "gauge"
}, {
  id: 5,
  title: "Traceable Origin",
  desc: "Every package features clear origin labeling, allowing you to trace your seafood back to the specific Florida coast it came from.",
  iconName: "search"
}, {
  id: 6,
  title: "Artisan Selection",
  desc: "Our expert fishmongers hand-inspect every fillet and shellfish to ensure only restaurant-grade quality enters our inventory.",
  iconName: "wrench"
}];
const partners = [{
  id: 1,
  name: "Florida Sustainable Fishing Certification",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1606399/img/partner_safety_board.png"
}, {
  id: 2,
  name: "The Villages Community Partner",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1606399/img/partner_mechanics_union.png"
}, {
  id: 3,
  name: "Florida Chamber of Commerce",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1606399/img/partner_global_inspection.png"
}, {
  id: 4,
  name: "Coastal Quality Alliance",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1606399/img/partner_quality_assurance.png"
}, {
  id: 5,
  name: "Fresh-Frozen Seafood Standard",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1606399/img/partner_trusted_dealer.png"
}];
const renderIcon = (name: string) => {
  switch (name) {
    case "wrench":
      return <Wrench className="size-6 text-primary" />;
    case "car":
      return <Car className="size-6 text-primary" />;
    case "shield":
      return <ShieldCheck className="size-6 text-primary" />;
    case "zap":
      return <Zap className="size-6 text-primary" />;
    case "search":
      return <Search className="size-6 text-primary" />;
    case "gauge":
      return <Gauge className="size-6 text-primary" />;
    default:
      return <ShieldCheck className="size-6 text-primary" />;
  }
};
export default function OurInspectionGuarantee() {
  const headerReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const gridReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const partnersReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="149766" id="trust-safety" className="dark bg-background text-foreground py-24 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        {}
        <div ref={headerReveal.ref} className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-1000 ease-out ${headerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold tracking-tight mb-6">
            Our Coastal Quality Commitment
          </h2>
          <p className="text-lg font-sans text-muted-foreground">
            At Village Seafood, we bring the bounty of the Florida coast directly to your freezer. 
            Our rigorous standards ensure that every delivery meets the highest expectations of The Villages community.
          </p>
        </div>

        {}
        <div ref={gridReveal.ref} className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-24 transition-all duration-1000 delay-200 ease-out ${gridReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {inspectionPoints.map((point, i) => <Card key={point.id} data-index={i} className="bg-card/50 backdrop-blur-sm border-border/40 hover:border-secondary/50 rounded-xl transition-all duration-300 shadow-sm hover:shadow-md">
              <CardHeader className="pb-4">
                <div className="size-12 rounded-lg bg-secondary/10 flex items-center justify-center mb-4">
                  {renderIcon(point.iconName)}
                </div>
                <CardTitle className="text-xl font-serif">{point.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground font-sans leading-relaxed">
                  {point.desc}
                </p>
              </CardContent>
            </Card>)}
        </div>

        {}
        <div ref={partnersReveal.ref} className={`border-t border-border/40 pt-16 transition-all duration-1000 delay-400 ease-out ${partnersReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="text-center text-sm font-sans font-medium text-muted-foreground uppercase tracking-widest mb-8">
            Certified & Trusted by Florida Maritime Authorities
          </p>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 lg:gap-24">
            {partners.map((partner, i) => <div key={partner.id} data-index={i} className="group relative flex items-center justify-center">
                <img src={partner.logo} alt={`${partner.name} logo`} className="h-10 md:h-12 w-auto object-contain opacity-40 grayscale transition-all duration-500 ease-in-out group-hover:opacity-100 group-hover:grayscale-0" />
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}